import { getCountryHomePage, getMarketCollectionPage } from '@/api/market';
import {
  fetchCountryHomePage,
  hasPendingEmptyCountryPage,
  usesCountryHomeFeed,
} from '@/domain/market/country-feed-pagination.js';
import { PAGE_SIZE, buildCollectionQuery } from '@/domain/market/feed-query';
import { fetchCollectionPageResult } from '@/domain/market/collection-page.js';
import { createFeedSourceAdapter } from '@/domain/market/feed-source-adapter.js';
import {
  fetchPagesUntilUpdownNormalized,
  normalizeHomeFeedRows,
  shouldNormalizeHomeFeedCategory,
} from '@/domain/crypto/crypto-minute-feed-pagination.js';

/** 首屏归集后少于此数视为未填满（旧逻辑 / flag 未生效时会只剩 1 张） */
const HOME_FEED_UNDERFILL_MIN = 8;

/** 兜底连拉上限：仅 underfill 时启用，groupBySeries 正常时不会走到 */
const HOME_FEED_FILL_MAX_PAGES = 20;

/**
 * 首页 Feed：
 * - 「全部 / Crypto 分类 / 加密分钟盘」无搜索：优先 groupBySeries 单次分页（币种×周期堆叠）
 * - 若归集后仍 underfill（缓存旧包 / 后端未识别 flag）：去掉 flag 连拉填满，避免只剩 1 张
 * - 其它 API 分类 / 搜索：原始分页（无 groupBySeries）
 */
export async function fetchHomeFeedPageResult(ctx) {
  if (usesCountryHomeFeed(ctx.state)) {
    return fetchCountryHomePage(ctx, getCountryHomePage);
  }
  const { query, append, pageNum, rawRowsLength, pageSize = PAGE_SIZE, state } = ctx;
  const primaryCategory = state?.primaryCategory;
  const hasSearch = Boolean(String(state?.appliedQuestion ?? '').trim());

  const result = await fetchCollectionPageResult({
    query,
    append,
    pageNum,
    rawRowsLength,
    pageSize,
  });

  // 搜索保持原始命中；分类/全部无搜索才做系列堆叠兜底
  if (hasSearch || !shouldNormalizeHomeFeedCategory(primaryCategory)) {
    return result;
  }

  const normalized = normalizeHomeFeedRows(result.apiRows, primaryCategory);

  if (!append && normalized.length < HOME_FEED_UNDERFILL_MIN && result.hasMore) {
    const fillQuery = { ...query };
    delete fillQuery.groupBySeries;
    return fetchPagesUntilUpdownNormalized({
      pageSize,
      startPageNum: 1,
      append: false,
      baselineRows: [],
      maxPages: HOME_FEED_FILL_MAX_PAGES,
      normalizeRows: (rows) => normalizeHomeFeedRows(rows, primaryCategory),
      fetchRawPage: async (page) => {
        const data = await getMarketCollectionPage(fillQuery, {
          pageNum: page,
          pageSize,
        });
        return { data, rows: Array.isArray(data?.rows) ? data.rows : [] };
      },
    });
  }

  return {
    ...result,
    apiRows: normalized,
  };
}

export function createHomeFeedFetchOptions() {
  return {
    ...createFeedSourceAdapter({
      logTag: 'marketFeed',
      pageSize: PAGE_SIZE,
      buildQuery: (store) => buildCollectionQuery(store),
      fetchPageResult: fetchHomeFeedPageResult,
    }),
    applyPageResult: (store, result) => {
      store.homeFeedCursor = result.feedCursor ?? null;
    },
    canLoadEmpty: hasPendingEmptyCountryPage,
  };
}
